import React, { useEffect, useState } from "react";
import { complement, filter, none, pipe, propEq, eqProps } from "ramda";
import { useDispatch, useSelector } from "react-redux";
import { firstError, hasError } from "common/validation";
import { isBlank, notBlank } from "common/general";
import * as steps from "common/steps";
import {
  PRIMARY_CATEGORY_CHANGED,
  SECONDARY_CATEGORY_CHANGED,
  TERTIARY_CATEGORY_CHANGED,
} from "file-details/fileDetailsActions";
import {
  getCategories,
  getStepErrors,
  getPrimaryCategory,
  getSecondaryCategory,
  getTertiaryCategory,
} from "common/selectors";
import Grid from "@material-ui/core/Grid";
import { useTranslation } from "react-i18next";

import IntlAutocomplete from "../../general-components/IntlAutocomplete";
import IntlTextField from "../../general-components/IntlTextField";

const Categories = ({ onBlur, language }) => {
  const { t } = useTranslation();
  const categories = useSelector(getCategories);
  const errors = useSelector(getStepErrors(steps.FILE_DETAILS));
  const primaryCategory = useSelector(getPrimaryCategory);
  const secondaryCategory = useSelector(getSecondaryCategory);
  const tertiaryCategory = useSelector(getTertiaryCategory);

  const [categoryOptions, setCategoryOptions] = useState([]);
  useEffect(() => {
    const usedCategories = filter(complement(isBlank), [
      primaryCategory,
      secondaryCategory,
      tertiaryCategory,
    ]);
    const options = pipe(
      filter(({ categoryId }) =>
        none(propEq("categoryId", categoryId), usedCategories)
      )
    )(categories);
    setCategoryOptions(options);
  }, [categories, primaryCategory, secondaryCategory, tertiaryCategory]);

  const dispatch = useDispatch();
  const onPrimaryCategoryChanged = (event, category) => {
    dispatch({ type: PRIMARY_CATEGORY_CHANGED, category });
  };
  const onSecondaryCategoryChanged = (event, category) =>
    dispatch({ type: SECONDARY_CATEGORY_CHANGED, category });
  const onTertiaryCategoryChanged = (event, category) =>
    dispatch({ type: TERTIARY_CATEGORY_CHANGED, category });

  return (
    notBlank(categories) && (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <IntlAutocomplete
            disableClearable
            autoSelect
            options={categoryOptions}
            value={primaryCategory || ""}
            getOptionLabel={(cat) => (notBlank(cat) ? cat.categoryName : "")}
            getOptionSelected={(opt, val) =>
              isBlank(val) || eqProps("categoryId", opt, val)
            }
            onChange={onPrimaryCategoryChanged}
            renderInput={(params) => {
              params.inputProps["aria-autocomplete"] = "none";
              params.inputProps["id"] = null;
              return (
                <IntlTextField
                  {...params}
                  type="text"
                  onBlur={onBlur("primaryCategory", primaryCategory)}
                  error={hasError(errors, "primaryCategory")}
                  helperText={t(firstError(errors, "primaryCategory"))}
                  name={"primaryCategory-" + Math.random()}
                  variant="standard"
                  label={t("categories_primary")}
                  fullWidth
                />
              );
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <IntlAutocomplete
            autoSelect
            options={categoryOptions}
            value={secondaryCategory || ""}
            getOptionLabel={(cat) => (notBlank(cat) ? cat.categoryName : "")}
            getOptionSelected={(opt, val) =>
              isBlank(val) || eqProps("categoryId", opt, val)
            }
            onChange={onSecondaryCategoryChanged}
            renderInput={(params) => {
              params.inputProps["aria-autocomplete"] = "none";
              params.inputProps["id"] = null;
              return (
                <IntlTextField
                  {...params}
                  type="text"
                  onBlur={onBlur("secondaryCategory", secondaryCategory)}
                  error={hasError(errors, "secondaryCategory")}
                  helperText={t(firstError(errors, "secondaryCategory"))}
                  name={"secondaryCategory-" + Math.random()}
                  variant="standard"
                  label={t("categories_secondary")}
                  fullWidth
                />
              );
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <IntlAutocomplete
            autoSelect
            options={categoryOptions}
            value={tertiaryCategory || ""}
            getOptionLabel={(cat) => (notBlank(cat) ? cat.categoryName : "")}
            getOptionSelected={(opt, val) =>
              isBlank(val) || eqProps("categoryId", opt, val)
            }
            onChange={onTertiaryCategoryChanged}
            renderInput={(params) => {
              params.inputProps["aria-autocomplete"] = "none";
              params.inputProps["id"] = null;
              return (
                <IntlTextField
                  {...params}
                  type="text"
                  onBlur={onBlur("tertiaryCategory", tertiaryCategory)}
                  error={hasError(errors, "tertiaryCategory")}
                  helperText={t(firstError(errors, "tertiaryCategory"))}
                  name={"secondaryCategory-" + Math.random()}
                  variant="standard"
                  label={t("categories_tertiary")}
                  fullWidth
                />
              );
            }}
          />
        </Grid>
        {language && (
          <Grid item xs={12} sm={6}>
            {language}
          </Grid>
        )}
      </Grid>
    )
  );
};

export default Categories;
